import { FC, lazy } from 'react';
import { REQUESTED_SUBMODULES_META, SUBMODULE_TYPE } from '@constants/constants';
import { MODULES_REQUEST_STATUS } from '@utils/types/RequestedDashboards';
import RequestableDashboard from './RequestableDashboard'; // Dashboard wrapper for requestable/approval-gated modules

/**********************************************************
 ** Lazy: ./GettingStartedDashboards/* (preview dashboards)
 **********************************************************/

// 1. Underwriting
const UnderwrittingPreview = lazy(
  () => import('./GettingStartedDashboards/GettingStartedUnderwriting'),
);
// 2. Funding eligibility
const FundingEligibilityPreview = lazy(
  () => import('./GettingStartedDashboards/GettingStartedFundingEligibility'),
);
// 3. Benchmarks
const BenchmarksPreview = lazy(() => import('./GettingStartedDashboards/GettingStartedBenchmarks'));
// 4. Financial projections
const FinancialProjectionsPreview = lazy(
  () => import('./GettingStartedDashboards/GettingStartedFinancialProjections'),
);
// 5. PvX report
const PvXReportsPreview = lazy(() => import('./GettingStartedDashboards/GettingStartedPvXReport'));

/**********************************************************
 ** Lazy: ./GettingStartedDashboards/* (demo dashboards)
 **********************************************************/

// 1. Getting started
const GettingStartedDemo = lazy(() => import('./CustomDashboards/GettingStartedDemo'));
// 2. Financial projections
const FinancialProjectionsPreviewDemo = lazy(
  () => import('../Demo/GettingStartedDashboards/GettingStartedFinancialProjections'),
);

/**********************************************************
 ** Lazy: ./components/FinancialProjectionsV2/*
 **********************************************************/

// 1. Current cohorts
const CurrentCohorts = lazy(() => import('./components/FinancialProjectionsV2/CurrentCohorts'));
// 2. Future cohorts
const FutureCohorts = lazy(() => import('./components/FinancialProjectionsV2/FutureCohorts'));
// 3. Model settings
const ModelSettings = lazy(() => import('./components/FinancialProjectionsV2/ModelSettings'));
// 4. Model requests
const ModelRequests = lazy(() => import('./components/FinancialProjectionsV2/ModelRequests'));

// 5. Financial projection
const FinancialProjection = lazy(() => import('./CustomDashboards/FinancialProjection'));

// 6. Financial projection v2
const FinancialProjectionV2 = lazy(() => import('./CustomDashboards/FinancialProjectionV2'));

// 7. Spend Impact
const SpendImpact = lazy(() => import('./CustomDashboards/SpendImpact'));

/**********************************************************
 ** Lazy: ./CustomDashboards/*
 **********************************************************/

// 1. Investment performance
const InvestmentPerformance = lazy(() => import('./CustomDashboards/InvestmentPerformance'));
// 2. Funding request
const FundingDetails = lazy(() => import('./CustomDashboards/FundingDetails'));
// 3. Benchmarks
const Benchmarks = lazy(() => import('./CustomDashboards/Benchmarks'));
// 4. Deal performance
const GamePerformance = lazy(() => import('./CustomDashboards/GamePerformance'));
// 5. Funding eligibility
const FundingEligibility = lazy(() => import('./CustomDashboards/FundingEligibility'));
// 6. Ad impressions
const AdImpressions = lazy(() => import('./CustomDashboards/AdImpressions'));
// 7. Accounting revenue
const AccountingRevenue = lazy(() => import('./CustomDashboards/AccountingRevenue'));
// 8. Getting started
const GettingStarted = lazy(() => import('./CustomDashboards/GettingStarted'));
// 9. Commissions
const CommissionsDashboard = lazy(() => import('./CustomDashboards/CommissionsDashboard'));
// 10. Payouts
const Payouts = lazy(() => import('./CustomDashboards/Payouts'));
// 11. Cohort analysis (underwriting)
const UnderwrittingDashboard = lazy(() => import('./CustomDashboards/UnderwrittingDashboard'));
// 12. Download reports
const DownloadReports = lazy(() => import('./CustomDashboards/DownloadReports'));
// 13. Financial reporting
const FinancialReporting = lazy(() => import('./CustomDashboards/FinancialReporting'));
// 14. Financial statements
const FinancialStatements = lazy(() => import('./CustomDashboards/FinancialStatements'));
// 15. Financing reports summary dashboard
const FinancingReports = lazy(() => import('./CustomDashboards/FinancingReports'));
// 16. Investor report dashboard
const InvestorReport = lazy(() => import('./CustomDashboards/InvestorReport'));
// 17. Self serve requests dashboard
const SelfServeRequestsDashboard = lazy(
  () => import('./CustomDashboards/SelfServeRequestsDashboard'),
);
// 18. Alerts dashboard
const AlertsDashboard = lazy(() => import('./CustomDashboards/AlertsDashboard'));
// 19. Due diligence dashboard
const DueDiligence = lazy(() => import('./CustomDashboards/DueDiligence'));
// 20. Market intelligence dashboard
const MarketIntelligenceDashboard = lazy(
  () => import('./CustomDashboards/MarketIntelligence/MarketIntelligenceDashboard'),
);
// 21. Market intelligence settings dashboard
const MarketIntelligenceSettings = lazy(
  () => import('./CustomDashboards/MarketIntelligence/MarketIntelligenceSettings'),
);
// 22. Referrer setup
const ReferrerSetup = lazy(() => import('./CustomDashboards/Referrer/ReferrerSetup'));
// 23. Funding request tracker (admin)
const FundingDetailsAdmin = lazy(() => import('../Admin/RequestTracker/FundingDetailsAdmin'));
// 24. Deal setup (admin)
const DealSetup = lazy(() => import('../Admin/DealSetup/DealSetup'));
// 25. Deal setup (viewer)
const DealSetupViewerDashboard = lazy(() => import('./CustomDashboards/DealSetupViewerDashboard'));
// 26. Exposure forecasting dashboard
const ExposureForecastingDashboard = lazy(
  () => import('./CustomDashboards/ExposureForecastingDashboard'),
);
// 27. Threshold breach alerts dashboard
const ThresholdBreachAlertsDashboard = lazy(
  () => import('./CustomDashboards/ThresholdBreachAlertsDashboard'),
);

// 28.  Liquidity providers setup
const LpSetup = lazy(() => import('./CustomDashboards/LpSetup'));
// 29. Manage users (client)
const ManageUsersDashboard = lazy(() => import('./CustomDashboards/ManageUsersDashboard'));
// 30. LP fund functions
const LPFundFunctions = lazy(() => import('./CustomDashboards/LPFundFunctions'));
// 31. Lp performance
const LpPerformance = lazy(() => import('./CustomDashboards/LpPerformance'));
// 32. Payins dashboard
const Payins = lazy(() => import('./CustomDashboards/Payins'));
// 33. PvX Impact dashboard
const PvxImpactDashboard = lazy(() => import('./CustomDashboards/PvxImpactDashboard'));
// 34. LambdaBot
const LambdaBot = lazy(() => import('./CustomDashboards/LambdaBot/LambdaBot'));

// 35. Investor report v2
const InvestorReportV2 = lazy(() => import('./CustomDashboards/InvestorReportV2'));

// 36. Investor report shared view
const InvestorReportSharedView = lazy(() => import('./CustomDashboards/InvestorReportSharedView'));

// 37. Market scan
const MarketScanner = lazy(() => import('./components/MarketScanner'));

interface CustomDashboardWrapperProps {
  id?: string;
  data: {
    id: string;
    name?: string;
    embedId?: string;
    submoduleType?: SUBMODULE_TYPE;
    filterQuery?: string;
    requestStatus: MODULES_REQUEST_STATUS;
    requestId?: string;
    isPreviewEnabled?: boolean;
  } | null;
  urlParams?: URLSearchParams;
}

const dashboardComponents: Record<
  SUBMODULE_TYPE,
  FC<{ id: string; role?: 'ADMIN' | 'USER'; itemId?: string; urlParams?: URLSearchParams }>
> = {
  [SUBMODULE_TYPE.INVESTMENT_PERFORMANCE]: InvestmentPerformance,
  [SUBMODULE_TYPE.FUNDING_REQUEST]: FundingDetails,
  [SUBMODULE_TYPE.FUNDING_REQUEST_TRACKER]: FundingDetailsAdmin,
  [SUBMODULE_TYPE.GAME_PERFORMANCE]: GamePerformance,
  [SUBMODULE_TYPE.BENCHMARKS_CUSTOM]: Benchmarks,
  [SUBMODULE_TYPE.FUNDING_ELIGIBILITY]: FundingEligibility,
  [SUBMODULE_TYPE.DEAL_SETUP]: DealSetup,
  [SUBMODULE_TYPE.AD_IMPRESSIONS]: AdImpressions,
  [SUBMODULE_TYPE.ACCOUNTING_REVENUE]: AccountingRevenue,
  [SUBMODULE_TYPE.GETTING_STARTED]: GettingStarted,
  [SUBMODULE_TYPE.GETTING_STARTED_DEMO]: GettingStartedDemo,
  [SUBMODULE_TYPE.COMMISSIONS_DASHBOARD]: CommissionsDashboard,
  [SUBMODULE_TYPE.DOWNLOADABLE_REPORTS]: DownloadReports,
  [SUBMODULE_TYPE.REFERRER_SETUP]: ReferrerSetup,
  [SUBMODULE_TYPE.COHORT_ANALYSIS]: UnderwrittingDashboard,
  [SUBMODULE_TYPE.FINANCIAL_REPORTING]: FinancialReporting,
  [SUBMODULE_TYPE.FINANCIAL_REPORTING_DASHBOARD]: FinancialStatements,
  [SUBMODULE_TYPE.DEAL_SETUP_DASHBOARD]: DealSetupViewerDashboard,
  [SUBMODULE_TYPE.FINANCING_REPORT_SUMMARY]: FinancingReports,
  [SUBMODULE_TYPE.INVESTOR_REPORT]: InvestorReport,
  [SUBMODULE_TYPE.INVESTOR_REPORT_V2]: InvestorReportV2,
  [SUBMODULE_TYPE.INVESTOR_REPORT_V2_EXT]: InvestorReportSharedView,
  [SUBMODULE_TYPE.SELF_SERVE]: SelfServeRequestsDashboard,
  [SUBMODULE_TYPE.FINANCIAL_PROJECTIONS]: FinancialProjection,
  [SUBMODULE_TYPE.FINANCIAL_PROJECTIONS_V2]: FinancialProjectionV2,
  [SUBMODULE_TYPE.CURRENT_COHORTS]: CurrentCohorts,
  [SUBMODULE_TYPE.FUTURE_COHORTS]: FutureCohorts,
  [SUBMODULE_TYPE.MODEL_REQUESTS]: ModelRequests,
  [SUBMODULE_TYPE.ALERTS_DASHBOARD]: AlertsDashboard,
  [SUBMODULE_TYPE.MODEL_SETTINGS]: ModelSettings,
  [SUBMODULE_TYPE.DUE_DILIGENCE]: DueDiligence,
  [SUBMODULE_TYPE.COMMISSIONS_PAYOUT]: Payouts,
  [SUBMODULE_TYPE.MARKET_INTELLIGENCE]: MarketIntelligenceDashboard,
  [SUBMODULE_TYPE.MARKET_INTELLIGENCE_SETTINGS]: MarketIntelligenceSettings,
  [SUBMODULE_TYPE.EXPOSURE_FORECASTING]: ExposureForecastingDashboard,
  [SUBMODULE_TYPE.THRESHOLD_BREACH_ALERTS]: ThresholdBreachAlertsDashboard,
  [SUBMODULE_TYPE.FUND_PERFORMANCE]: LpPerformance,
  [SUBMODULE_TYPE.LP_SETUP]: LpSetup,
  [SUBMODULE_TYPE.FUND_ALLOCATION]: LPFundFunctions,
  [SUBMODULE_TYPE.CLIENT_USER_MANAGEMENT]: ManageUsersDashboard,
  [SUBMODULE_TYPE.COMMISSIONS_PAYINS]: Payins,
  [SUBMODULE_TYPE.IMPACT_REPORT]: PvxImpactDashboard,
  [SUBMODULE_TYPE.LAMBDA_INTELLIGENCE]: LambdaBot,
  [SUBMODULE_TYPE.SPEND_IMPACT]: SpendImpact,
  [SUBMODULE_TYPE.MARKET_SCAN]: MarketScanner,
};

const previewDashboardComponents: Record<string, FC> = {
  UNDERWRITTING: UnderwrittingPreview,
  FUNDING_ELIGIBILITY: FundingEligibilityPreview,
  BENCHMARKS: BenchmarksPreview,
  FINANCIAL_PROJECTIONS: FinancialProjectionsPreview,
  FINANCIAL_PROJECTIONS_DEMO: FinancialProjectionsPreviewDemo,
  INVESTOR_REPORT: PvXReportsPreview,
};

const CustomDashboardWrapper: FC<CustomDashboardWrapperProps> = ({ id, data, urlParams }) => {
  if (!data?.submoduleType || !data.id) {
    return null;
  }

  if (data.requestStatus !== 'APPROVED') {
    return (
      <RequestableDashboard
        title={REQUESTED_SUBMODULES_META[data.submoduleType].title}
        subTitle={REQUESTED_SUBMODULES_META[data.submoduleType].subTitle}
        body={REQUESTED_SUBMODULES_META[data.submoduleType].body}
        requestStatus={data.requestStatus}
        images={REQUESTED_SUBMODULES_META[data.submoduleType].images}
        requestId={data.requestId as string}
      />
    );
  }

  const DashboardComponent = dashboardComponents[data.submoduleType];
  const PreviewDashboardComponent = previewDashboardComponents[data.submoduleType];

  if (!!data.isPreviewEnabled) {
    return <PreviewDashboardComponent />;
  }

  return DashboardComponent ? (
    <DashboardComponent id={data.id} itemId={id} role={'ADMIN'} urlParams={urlParams} />
  ) : (
    <></>
  );
};

export default CustomDashboardWrapper;
